<template>
  <div
    class="report-modal-wrapper"
    :class="{ mobile: isMobile }"
    v-if="showReportModal"
  >
    <div class="report-modal">
      <div class="report-modal__content">
        <div class="report-modal__header">
          <h5 class="report-modal__title">
            {{ step.title }}
          </h5>
          <span
            aria-hidden="true"
            class="report-modal__close"
            @click="closeModal"
            >&times;</span
          >
        </div>
        <div
          class="report-modal__body"
          :class="{ 'with-footer': step.showFooter }"
        >
          <Spinner v-if="showSpinner" class="spinner"></Spinner>
          <template v-else>
            <p
              v-if="
                currentStep === 'DEFAULT' || currentStep === 'DEFAULT_EXIST'
              "
            >
              Select the option you prefer:
            </p>
            <div class="step--0" v-if="currentStep === 'DEFAULT'">
              <NotSetCard
                @onButtonClick="startSetup()"
                v-if="!showError"
              ></NotSetCard>
              <Notification v-else title="error" type="error">
                Sorry, an error has occurred and we are investigating it. Please
                try again later.
              </Notification>
              <DownloadCard></DownloadCard>
            </div>
            <div
              class="step--0 extra-padding"
              v-if="currentStep === 'DEFAULT_CREATED'"
            >
              <CreatedCard
                :data="currentSetting"
                @onEdit="startEdit()"
                @onDelete="onDelete"
              ></CreatedCard>
              <div
                class="help-text"
                v-if="accountList.length > 1 || asnList.length > 1"
              >
                <p>Want to setup a recurring report for another origin AS?</p>
                <span @click="showHelpModal = true">View steps</span>
              </div>

              <DownloadCard></DownloadCard>
            </div>
            <div class="step--0" v-if="currentStep === 'DEFAULT_EXIST'">
              <ExistCard
                :data="currentSetting"
                @onDelete="onDelete"
                @onEdit="startEdit()"
              ></ExistCard>
              <DownloadCard></DownloadCard>
            </div>
            <div class="step--0" v-if="currentStep === 'DEFAULT_UPDATED'">
              <UpdatedCard
                :data="currentSetting"
                @onDelete="onDelete"
                @onEdit="startEdit()"
              ></UpdatedCard>
              <DownloadCard></DownloadCard>
            </div>
            <div class="step--0" v-if="currentStep === 'DEFAULT_DELETED'">
              <DeletedCard @onButtonClick="startSetup()"></DeletedCard>
              <DownloadCard></DownloadCard>
            </div>
            <div class="step--1" v-if="currentStep === 'SETUP_FORM'">
              <Form @onFormUpdate="updateForm($event)" :form="form"></Form>
            </div>
            <div class="step--2" v-if="currentStep === 'SETUP_CONFIRM'">
              <p><b>Review your report preferences: </b></p>
              <p>{{ review[0] }}</p>
              <p>{{ review[1] }}</p>
            </div>
            <div class="step--3" v-if="currentStep === 'DELETE'">
              <h6>
                <fa icon="exclamation-triangle" class="icon"></fa>You will not
                receive recurring report
              </h6>
              <p>
                Do you want to cancel the recurring report of your network’s top
                offending IP addresses for origin AS number?
              </p>
            </div>
            <div class="step--1" v-if="currentStep === 'EDIT_FORM'">
              <Form @onFormUpdate="updateForm($event)" :form="form"></Form>
            </div>
            <div class="step--2" v-if="currentStep === 'EDIT_CONFIRM'">
              <p><b>Review your report preferences: </b></p>
              <p>{{ review[0] }}</p>
              <p>{{ review[1] }}</p>
            </div>
          </template>
        </div>
        <div class="report-modal__footer" v-if="step.showFooter">
          <button
            type="button"
            class="btn btn-outline-primary"
            @click="onBack(step.backStep)"
          >
            {{ step.backText }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="onNext(step.nextStep)"
          >
            {{ step.okText }}
          </button>
        </div>
      </div>
    </div>
    <help-modal
      v-if="showHelpModal"
      @close="showHelpModal = false"
    ></help-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import * as vClickOutside from 'v-click-outside';
import DownloadCard from './Card/DownloadCard';
import NotSetCard from './Card/NotSetCard';
import CreatedCard from './Card/CreatedCard';
import ExistCard from './Card/ExistCard';
import DeletedCard from './Card/DeletedCard';
import UpdatedCard from './Card/UpdatedCard';
import Form from './Setup/Form';
import Spinner from './../../ui/Spinner';
import Notification from './../../ui/Notification';
import {
  getPartialScheduleText,
  STEP,
  STEPS,
  getSettingFromExpression,
  getExpressionFromSetting
} from './helper.js';
import HelpModal from './HelpModal';

export default {
  data() {
    return {
      STEPS,
      currentStep: STEP.DEFAULT,
      form: {
        period: '30',
        frequency: 'Daily',
        cron: {
          day: '1',
          month: '1',
          week: 'Mon'
        }
      },
      currentSetting: null,
      review: ['', ''],
      showHelpModal: false,
      showSpinner: false,
      showError: false
    };
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
  watch: {
    showReportModal(val) {
      if (val) {
        this.asyncHelper(this.fetchRecurringReport(), () => {
          if (this.recurringExpression) {
            this.currentSetting = getSettingFromExpression(
              this.recurringExpression
            );
            this.currentSetting.period = this.recurringPeriod;
            this.currentStep = STEP.DEFAULT_EXIST;
          } else {
            this.currentStep = STEP.DEFAULT;
          }
        });
      }
    }
  },
  computed: {
    ...mapGetters(['showReportModal', 'isMobile']),
    ...mapGetters('dataStore', [
      'recurringExpression',
      'recurringPeriod',
      'accountList',
      'asnList'
    ]),
    step() {
      return this.STEPS[this.currentStep];
    }
  },
  methods: {
    ...mapActions('dataStore', [
      'fetchRecurringReport',
      'createRecurringReport',
      'updateRecurringReport',
      'deleteRecurringReport'
    ]),
    asyncHelper(promise, onSuccess) {
      this.showSpinner = true;
      this.showError = false;
      promise.then(res => {
        if (res) {
          onSuccess();
        } else {
          this.showError = true;
          this.currentStep = STEP.DEFAULT;
        }
        this.showSpinner = false;
      });
    },
    closeModal() {
      this.$store.commit('setShowReportModal', false);
      this.currentStep = STEP.DEFAULT;
    },
    startSetup() {
      this.currentStep = STEP.SETUP_FORM;
    },
    startEdit() {
      this.currentStep = STEP.EDIT_FORM;
      if (this.currentSetting) {
        this.form = JSON.parse(JSON.stringify(this.currentSetting));
      }
    },
    onBack(step) {
      this.currentStep = step;
    },
    onNext(step) {
      if (step === STEP.SETUP_CONFIRM || step === STEP.EDIT_CONFIRM) {
        this.review = this.generateReivewText();
      }
      if (
        this.currentStep === STEP.SETUP_CONFIRM &&
        step === STEP.DEFAULT_CREATED
      ) {
        const expression = getExpressionFromSetting(this.form);

        // trigger event in Google Tag Manager
        window.dataLayer.push({
          event: 'CREATED_RECURRING_REPORT',
          frequency: expression || '',
          period: this.form.period || ''
        });

        this.asyncHelper(
          this.createRecurringReport({
            expression,
            period: Number(this.form.period)
          }),
          () => {
            this.currentSetting = JSON.parse(JSON.stringify(this.form));
          }
        );
      }
      if (
        this.currentStep === STEP.EDIT_CONFIRM &&
        step === STEP.DEFAULT_UPDATED
      ) {
        const expression = getExpressionFromSetting(this.form);
        this.asyncHelper(
          this.updateRecurringReport({
            expression,
            period: Number(this.form.period)
          }),
          () => {
            this.currentSetting = JSON.parse(JSON.stringify(this.form));
          }
        );
      }
      if (step === STEP.DEFAULT_DELETED) {
        this.asyncHelper(this.deleteRecurringReport(), () => {
          this.currentSetting = null;
        });
      }
      this.currentStep = step;
    },
    onDelete() {
      this.currentStep = STEP.DELETE;
    },
    updateForm(data) {
      this.form = data;
    },
    generateReivewText() {
      let lineOne = 'You will receive your report ';
      let lineTwo = 'The report will show data for the latest ';

      lineOne += getPartialScheduleText(this.form);
      lineOne += '.';
      lineTwo += `${this.form.period} days.`;

      return [lineOne, lineTwo];
    }
  },
  components: {
    DownloadCard,
    NotSetCard,
    Form,
    CreatedCard,
    ExistCard,
    DeletedCard,
    UpdatedCard,
    HelpModal,
    Spinner,
    Notification
  }
};
</script>

<style lang="scss" scoped>
.report-modal-wrapper {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba($color: #000000, $alpha: 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9990;
}

.report-modal {
  background: white;
  position: relative;
  min-width: 635px;
  overflow-y: auto;

  @media screen and (max-width: 735px) {
    min-width: 300px;
    max-height: 90vh;
  }

  .icon {
    color: #ffc107;
    margin-right: 4px;
  }

  .spinner {
    width: 100%;
    height: 200px;
  }

  &__header,
  &__body,
  &__footer {
    padding: 1rem;
  }

  &__header {
    border-bottom: 1px solid #b3b3b3;
    padding-right: 32px;
  }

  &__footer {
    border-top: 1px solid #b3b3b3;
    text-align: right;
    button {
      margin: 4px;
    }
  }

  &__title {
    margin-bottom: 0;
  }

  &__close {
    position: absolute;
    right: 8px;
    top: 8px;
    font-weight: 700;
    font-size: 24px;
    color: #000000;
    cursor: pointer;
    line-height: 1;
  }

  .step {
    &--0 {
      display: flex;
      position: relative;
      flex-wrap: wrap;

      &.extra-padding {
        padding-bottom: 3rem;
      }

      .help-text {
        position: absolute;
        bottom: 0;
        padding: 16px;
        p {
          margin: 0;
        }
        span {
          color: $apnic-blue;
          cursor: pointer;
        }
      }
      & > div {
        flex: 1;
      }

      @media screen and (max-width: 735px) {
        flex-direction: column;
        align-items: center;

        .help-text {
          position: relative;
        }
      }
    }

    &--3 {
      width: 600px;
      max-width: 100%;
    }
  }
}

.mobile.report-modal-wrapper {
  .report-modal {
    min-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 100%;
    overflow: hidden;

    &__content {
      width: 100%;
      height: 100%;
      position: relative;
    }

    &__header {
      position: fixed;
      top: 0;
      width: 100%;
      height: 72px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-right: 0;

      h5 {
        flex: 1;
      }

      span {
        position: initial;
        padding: 1rem;
      }
    }

    &__body {
      position: absolute;
      top: 80px;
      width: 100%;
      height: calc(100% - 72px);
      overflow-y: scroll;

      &.with-footer {
        height: calc(100% - 144px);
      }
    }

    &__footer {
      position: fixed;
      bottom: 0;
      width: 100%;
      height: 72px;
      display: flex;
      justify-content: space-evenly;
    }
  }
}
</style>
